import { useEffect, useState } from "react";
import api from "@/lib/api";

const dayLabels = {
  "2027-07-02": "Vendredi 2 (Libération)",
  "2027-07-03": "Samedi 3 (Lumière)",
  "2027-07-04": "Dimanche 4 (Unité)",
};
const formulaLabels = {
  jour: "Journée découverte",
  pass3j: "Pass 3 jours",
  pass3j_repas: "Pass 3 jours + repas",
  solidaire: "Tarif solidaire",
};
const workshopLabels = {
  icone: "Icône", sculpture: "Sculpture", chants: "Chants", botanique: "Botanique",
};
const colorMap = { cinnabar: "var(--cinnabar)", gold: "var(--gold)", lapis: "var(--lapis)" };

function CheckBadge({ checked, onClick, label, testId, color }) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={!!checked}
      onClick={onClick}
      className="font-caps text-[0.6rem] px-2 py-1 transition"
      style={{
        background: checked ? color : "transparent",
        color: checked ? "white" : "var(--muted)",
        border: `1px solid ${checked ? color : "var(--border)"}`,
        letterSpacing: "0.12em",
      }}
      title={label}
    >
      {checked ? "✓ " : ""}{label}
    </button>
  );
}

export function PresenceRegistrationsTable({ items, onItemUpdated }) {
  const patch = async (sessionId, field, currentValue) => {
    const body = { [field]: !currentValue };
    const { data } = await api.patch(`/admin/presence/registrations/${sessionId}`, body);
    onItemUpdated(data);
  };
  if (items.length === 0) {
    return (
      <p data-testid="presence-empty" className="font-body italic" style={{ color: "var(--muted)" }}>
        Aucune inscription pour ces critères.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full bg-white" style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid var(--border)" }}>
            {["Date", "Nom", "Email", "Tél.", "Formule", "Jour", "Atelier", "Pers.", "Montant", "Paie.", "Check-in"].map((h) => (
              <th key={h} className="label-caps text-left p-3 whitespace-nowrap" style={{ color: "var(--muted)" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.session_id} data-testid={`presence-row-${it.session_id}`}
                style={{ borderBottom: "1px solid var(--border)" }}>
              <td className="p-3 font-body text-xs whitespace-nowrap">
                {new Date(it.created_at).toLocaleDateString("fr-FR")}
              </td>
              <td className="p-3 font-body whitespace-nowrap">{it.name}</td>
              <td className="p-3 font-body text-xs">
                <a href={`mailto:${it.email}`} className="link-underline">{it.email}</a>
              </td>
              <td className="p-3 font-body text-xs whitespace-nowrap">{it.phone || "—"}</td>
              <td className="p-3 font-body text-xs whitespace-nowrap">{formulaLabels[it.formula] || it.formula}</td>
              <td className="p-3 font-body text-xs whitespace-nowrap">{dayLabels[it.day_choice] || "—"}</td>
              <td className="p-3 font-body text-xs whitespace-nowrap">{workshopLabels[it.workshop] || "—"}</td>
              <td className="p-3 font-body text-center">{it.quantity}</td>
              <td className="p-3 font-display whitespace-nowrap">{it.amount?.toFixed(2)} €</td>
              <td className="p-3">
                <span className="font-caps text-[0.6rem]" style={{
                  color: it.payment_status === "paid" ? "var(--moss)" : "var(--muted)"
                }}>{it.payment_status}</span>
              </td>
              <td className="p-3">
                <div className="flex flex-wrap gap-1">
                  <CheckBadge
                    checked={!!it.validated}
                    onClick={() => patch(it.session_id, "validated", !!it.validated)}
                    label="Validée"
                    testId={`checkin-validated-${it.session_id}`}
                    color="var(--moss)"
                  />
                  <CheckBadge
                    checked={!!it.present}
                    onClick={() => patch(it.session_id, "present", !!it.present)}
                    label="Présent"
                    testId={`checkin-present-${it.session_id}`}
                    color="var(--lapis)"
                  />
                  {it.formula === "pass3j_repas" && (
                    <CheckBadge
                      checked={!!it.meals_taken}
                      onClick={() => patch(it.session_id, "meals_taken", !!it.meals_taken)}
                      label="Repas"
                      testId={`checkin-meals-${it.session_id}`}
                      color="var(--cinnabar)"
                    />
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PresencePlanningView() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/presence/planning").then((r) => setData(r.data)); }, []);
  if (!data) return <p className="font-body" style={{ color: "var(--muted)" }}>Chargement…</p>;

  return (
    <div data-testid="presence-planning" className="space-y-8">
      <p className="font-body" style={{ color: "var(--ink-2)" }}>
        Vue jour-par-jour avec le nombre d'inscrits attendus à chaque temps fort. Les chiffres en
        <span style={{ color: "var(--moss)" }}> vert</span> sont les paiements confirmés ;
        en <span style={{ color: "var(--muted)" }}>gris</span> les paiements en attente.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {data.days.map((day) => {
          const c = colorMap[day.color];
          return (
            <div key={day.date} className="bg-white border" style={{ borderColor: "var(--border)", borderTop: `4px solid ${c}` }}>
              <div className="p-5 border-b" style={{ borderColor: "var(--border)" }}>
                <div className="font-caps text-[0.7rem]" style={{ color: c }}>
                  {new Date(day.date).toLocaleDateString("fr-FR", {weekday:"long", day:"numeric", month:"long"})}
                </div>
                <h3 className="font-display text-2xl mt-1">{day.theme}</h3>
                <div className="grid grid-cols-3 gap-2 mt-4">
                  <div className="text-center p-2" style={{ background: "var(--parchment-2)" }}>
                    <div className="label-caps" style={{ color: "var(--muted)", fontSize: "0.58rem" }}>Présents</div>
                    <div className="font-display text-2xl">
                      <span style={{ color: "var(--moss)" }}>{day.attendees_paid}</span>
                      <span style={{ color: "var(--muted)", fontSize: "0.85rem" }}> +{day.attendees_pending}</span>
                    </div>
                  </div>
                  <div className="text-center p-2" style={{ background: "var(--parchment-2)" }}>
                    <div className="label-caps" style={{ color: "var(--muted)", fontSize: "0.58rem" }}>Repas</div>
                    <div className="font-display text-2xl">
                      <span style={{ color: "var(--moss)" }}>{day.meals_paid}</span>
                      <span style={{ color: "var(--muted)", fontSize: "0.85rem" }}> +{day.meals_pending}</span>
                    </div>
                  </div>
                  <div className="text-center p-2" style={{ background: "var(--parchment-2)" }}>
                    <div className="label-caps" style={{ color: "var(--muted)", fontSize: "0.58rem" }}>Ateliers</div>
                    <div className="font-display text-2xl">
                      {Object.values(day.workshops || {}).reduce((a, b) => a + b, 0)}
                    </div>
                  </div>
                </div>
                {Object.keys(day.workshops || {}).length > 0 && (
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {Object.entries(day.workshops).map(([w, n]) => (
                      <span key={w} className="tag tag-gold">{workshopLabels[w] || w}: {n}</span>
                    ))}
                  </div>
                )}
              </div>
              <ol className="p-5 space-y-4">
                {day.slots.map((slot, i) => (
                  <li key={i} className="flex gap-4 items-start" data-testid={`slot-${day.date}-${slot.time}`}>
                    <div className="shrink-0 w-14">
                      <div className="font-caps text-[0.7rem]" style={{ color: c }}>{slot.time}</div>
                    </div>
                    <div className="flex-1">
                      <div className="font-body text-sm font-medium">{slot.label}</div>
                      <div className="font-body text-xs italic" style={{ color: "var(--muted)" }}>{slot.place}</div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="font-display text-lg">
                        <span style={{ color: "var(--moss)" }}>{slot.attendees_paid}</span>
                        {slot.attendees_pending > 0 && (
                          <span style={{ color: "var(--muted)", fontSize: "0.75rem" }}> +{slot.attendees_pending}</span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function PresencePreRegistrations() {
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = () => {
    const params = new URLSearchParams();
    if (statusFilter !== "all") params.set("status", statusFilter);
    api.get(`/admin/presence/pre-registrations?${params.toString()}`).then((r) => setData(r.data));
  };
  useEffect(load, [statusFilter]); // eslint-disable-line

  const setStatus = async (id, newStatus) => {
    await api.patch(`/admin/presence/pre-registrations/${id}`, { status: newStatus });
    load();
  };

  const sendInvitation = async (id, name) => {
    if (!window.confirm(`Envoyer l'email d'ouverture des inscriptions à ${name} ?`)) return;
    try {
      const r = await api.post(`/admin/presence/pre-registrations/${id}/invite-registration`);
      alert(r.data.email_sent
        ? `Invitation envoyée à ${name}.`
        : `Invitation enregistrée (email en attente — Brevo non configuré).`);
      load();
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Erreur");
    }
  };

  const sendReminder = async (id, name) => {
    if (!window.confirm(`Envoyer une relance douce à ${name} ?\n(Le ton du message reste contemplatif, sans urgence ni démarche commerciale.)`)) return;
    try {
      const r = await api.post(`/admin/presence/pre-registrations/${id}/remind`);
      alert(r.data.email_sent
        ? `Relance envoyée à ${name} (total : ${r.data.reminders_sent}).`
        : `Relance enregistrée (email en attente).`);
      load();
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Erreur");
    }
  };

  const downloadCsv = async () => {
    const url = `${api.defaults.baseURL}/admin/presence/pre-registrations.csv`;
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) { alert("Erreur export"); return; }
    const blob = await res.blob();
    const a = document.createElement("a");
    const u = URL.createObjectURL(blob);
    a.href = u;
    a.download = res.headers.get("Content-Disposition")?.match(/filename="([^"]+)"/)?.[1] || "pre-inscriptions.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(u);
  };

  if (!data) return <p className="font-body" style={{ color: "var(--muted)" }}>Chargement…</p>;

  const statusColors = {
    pending: "var(--gold)", contacted: "var(--lapis)", invited: "var(--cinnabar)",
    converted: "var(--moss)", declined: "var(--muted)", cancelled: "var(--muted)",
  };
  const ageHeader = (lbl) => (
    <th className="label-caps text-center p-2 whitespace-nowrap" style={{ color: "var(--muted)" }}>{lbl}</th>
  );

  return (
    <div data-testid="prereg-admin" className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="admin-card text-center">
          <div className="label-caps mb-1" style={{ color: "var(--muted)" }}>Total inscrits</div>
          <div className="font-display text-3xl">{data.stats.total}</div>
        </div>
        <div className="admin-card text-center">
          <div className="label-caps mb-1" style={{ color: "var(--muted)" }}>Personnes</div>
          <div className="font-display text-3xl">{data.stats.total_people}</div>
        </div>
        <div className="admin-card text-center">
          <div className="label-caps mb-1" style={{ color: "var(--muted)" }}>Pays représentés</div>
          <div className="font-display text-3xl">{Object.keys(data.stats.by_country).length}</div>
        </div>
        <div className="admin-card">
          <div className="label-caps mb-2" style={{ color: "var(--muted)" }}>Par statut</div>
          <div className="flex flex-wrap gap-1">
            {Object.entries(data.stats.by_status).map(([s, n]) => (
              <span key={s} className="font-caps text-[0.6rem] px-2 py-1"
                    style={{ background: statusColors[s] || "var(--border)", color: "white" }}>
                {s} {n}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="admin-card flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="label-caps">Statut :</span>
          <select data-testid="prereg-filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="input-classic" style={{ background: "transparent", width: "auto" }}>
            <option value="all">Tous</option>
            <option value="pending">En attente</option>
            <option value="contacted">Contactés</option>
            <option value="invited">Invités</option>
            <option value="converted">Convertis</option>
            <option value="declined">Refusés</option>
            <option value="cancelled">Annulés</option>
          </select>
        </div>
        <div className="ml-auto">
          <button data-testid="prereg-export-csv" onClick={downloadCsv} className="btn btn-primary">⬇ Export CSV</button>
        </div>
      </div>

      {data.items.length === 0 ? (
        <p className="font-body italic" style={{ color: "var(--muted)" }}>Aucune pré-inscription.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full bg-white" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)" }}>
                <th className="label-caps text-left p-2 whitespace-nowrap" style={{ color: "var(--muted)" }}>Date</th>
                <th className="label-caps text-left p-2 whitespace-nowrap" style={{ color: "var(--muted)" }}>Prénom · Nom</th>
                <th className="label-caps text-left p-2 whitespace-nowrap" style={{ color: "var(--muted)" }}>Email</th>
                <th className="label-caps text-left p-2 whitespace-nowrap" style={{ color: "var(--muted)" }}>Ville · Pays</th>
                {ageHeader("<6")}
                {ageHeader("6-12")}
                {ageHeader("13-17")}
                {ageHeader("Ad.")}
                {ageHeader("65+")}
                <th className="label-caps text-center p-2 whitespace-nowrap" style={{ color: "var(--muted)" }}>Tot.</th>
                <th className="label-caps text-left p-2 whitespace-nowrap" style={{ color: "var(--muted)" }}>Statut</th>
                <th className="label-caps text-right p-2 whitespace-nowrap" style={{ color: "var(--muted)" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => {
                const fullName = it.first_name || it.last_name
                  ? `${it.first_name || ""} ${it.last_name || ""}`.trim()
                  : it.name;
                const canInvite = ["pending", "contacted"].includes(it.status);
                const canRemind = it.status === "invited";
                return (
                <tr key={it.id} data-testid={`prereg-row-${it.id}`}
                    style={{ borderBottom: "1px solid var(--border)" }}>
                  <td className="p-2 font-body text-xs whitespace-nowrap">
                    {new Date(it.created_at).toLocaleDateString("fr-FR")}
                  </td>
                  <td className="p-2 font-body">{fullName}</td>
                  <td className="p-2 font-body text-xs"><a href={`mailto:${it.email}`} className="link-underline">{it.email}</a></td>
                  <td className="p-2 font-body text-xs">
                    {it.city ? `${it.city} · ` : ""}{it.country}
                  </td>
                  <td className="p-2 font-body text-center text-xs">{it.nb_under_6 || 0}</td>
                  <td className="p-2 font-body text-center text-xs">{it.nb_6_12 || 0}</td>
                  <td className="p-2 font-body text-center text-xs">{it.nb_13_17 || 0}</td>
                  <td className="p-2 font-body text-center text-xs">{it.nb_adult || 0}</td>
                  <td className="p-2 font-body text-center text-xs">{it.nb_senior || 0}</td>
                  <td className="p-2 font-body text-center font-display">{it.nb_people}</td>
                  <td className="p-2">
                    <span className="font-caps text-[0.6rem] px-2 py-1 inline-block"
                          style={{ background: statusColors[it.status] || "var(--border)", color: "white" }}>
                      {it.status}
                    </span>
                    {it.reminders_sent ? (
                      <span className="ml-1 font-caps text-[0.55rem]" style={{ color: "var(--muted)" }}>
                        · {it.reminders_sent}×↻
                      </span>
                    ) : null}
                  </td>
                  <td className="p-2">
                    <div className="flex gap-1 justify-end flex-wrap">
                      {canInvite && (
                        <button data-testid={`prereg-invite-${it.id}`}
                                onClick={() => sendInvitation(it.id, fullName)}
                                className="font-caps text-[0.55rem] px-2 py-1 border"
                                title="Envoyer l'email d'ouverture des inscriptions"
                                style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)", letterSpacing: "0.15em" }}>
                          ✉ INVITER
                        </button>
                      )}
                      {canRemind && (
                        <button data-testid={`prereg-remind-${it.id}`}
                                onClick={() => sendReminder(it.id, fullName)}
                                className="font-caps text-[0.55rem] px-2 py-1 border"
                                title="Relance douce et non-commerciale"
                                style={{ borderColor: "var(--lapis)", color: "var(--lapis)", letterSpacing: "0.15em" }}>
                          ↻ RELANCER
                        </button>
                      )}
                      <select data-testid={`prereg-status-${it.id}`} value={it.status}
                              onChange={(e) => setStatus(it.id, e.target.value)}
                              className="font-caps text-[0.6rem]"
                              style={{ background: "transparent", border: "1px solid var(--border)", padding: "2px 4px" }}>
                        <option value="pending">pending</option>
                        <option value="contacted">contacted</option>
                        <option value="invited">invited</option>
                        <option value="converted">converted</option>
                        <option value="declined">declined</option>
                        <option value="cancelled">cancelled</option>
                      </select>
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function PresenceCheckinSheet() {
  const [day, setDay] = useState("2027-07-02");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/admin/presence/checkin-sheet?day=${day}`).then((r) => setData(r.data));
  }, [day]);

  const dayOptions = {
    "2027-07-02": "Vendredi 2 juillet — Libération",
    "2027-07-03": "Samedi 3 juillet — Lumière",
    "2027-07-04": "Dimanche 4 juillet — Unité",
  };

  return (
    <div data-testid="presence-checkin-sheet" className="space-y-6">
      <div className="flex items-center gap-4 flex-wrap print:hidden">
        <span className="label-caps">Journée :</span>
        <select
          data-testid="sheet-day"
          value={day}
          onChange={(e) => setDay(e.target.value)}
          className="input-classic"
          style={{ background: "transparent", width: "auto" }}
        >
          {Object.entries(dayOptions).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <div className="ml-auto">
          <button data-testid="sheet-print" onClick={() => window.print()} className="btn btn-primary">
            🖨 Imprimer la fiche
          </button>
        </div>
      </div>

      {!data ? (
        <p className="font-body" style={{ color: "var(--muted)" }}>Chargement…</p>
      ) : (
        <div className="bg-white p-8 print:p-0" style={{ border: "1px solid var(--border)" }}>
          {/* Header (visible at print) */}
          <div className="text-center mb-6 pb-4 border-b" style={{ borderColor: "var(--border)" }}>
            <div className="font-caps text-[0.7rem]" style={{ color: "var(--cinnabar)" }}>
              Fiche d'émargement — Fêtes de saint Léonard 2027
            </div>
            <h2 className="font-display text-3xl mt-1">{data.day_label}</h2>
            <p className="font-body text-sm mt-2" style={{ color: "var(--muted)" }}>
              {data.count_paid} payé(s) · {data.count_pending} en attente · {data.items.length} inscription(s)
            </p>
          </div>

          {data.items.length === 0 ? (
            <p className="text-center font-body italic py-8" style={{ color: "var(--muted)" }}>
              Aucune personne inscrite pour cette journée.
            </p>
          ) : (
            <table className="w-full" style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  <th className="label-caps text-left p-2" style={{ color: "var(--muted)" }}>#</th>
                  <th className="label-caps text-left p-2" style={{ color: "var(--muted)" }}>Nom</th>
                  <th className="label-caps text-left p-2" style={{ color: "var(--muted)" }}>Formule</th>
                  <th className="label-caps text-left p-2" style={{ color: "var(--muted)" }}>Tél.</th>
                  <th className="label-caps text-left p-2" style={{ color: "var(--muted)" }}>Pers.</th>
                  <th className="label-caps text-left p-2" style={{ color: "var(--muted)" }}>Paiement</th>
                  <th className="label-caps text-left p-2" style={{ color: "var(--muted)" }}>Signature</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it, i) => (
                  <tr key={it.session_id} style={{ borderBottom: "1px dashed var(--border)" }}>
                    <td className="p-2 font-body text-sm">{i + 1}</td>
                    <td className="p-2 font-body">{it.name}</td>
                    <td className="p-2 font-body text-xs">
                      {it.formula === "jour" ? "Journée" :
                       it.formula === "pass3j" ? "Pass 3j" :
                       it.formula === "pass3j_repas" ? "Pass 3j + repas" : "Solidaire"}
                      {it.workshop && day === "2027-07-02" ? ` (${it.workshop})` : ""}
                    </td>
                    <td className="p-2 font-body text-xs">{it.phone || "—"}</td>
                    <td className="p-2 font-body text-center">{it.quantity}</td>
                    <td className="p-2 font-caps text-[0.62rem]"
                        style={{ color: it.payment_status === "paid" ? "var(--moss)" : "var(--cinnabar)" }}>
                      {it.payment_status === "paid" ? "✓" : "À régler"}
                    </td>
                    <td className="p-2" style={{ minWidth: "180px", borderBottom: "1px solid var(--ink)" }}>
                      &nbsp;
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="mt-8 pt-4 border-t font-body text-xs italic"
               style={{ borderColor: "var(--border)", color: "var(--muted)" }}>
            Imprimé le {new Date().toLocaleString("fr-FR")} — Les Amis du Prieuré saint Léonard
          </div>
        </div>
      )}
    </div>
  );
}

export function PresenceEmailModal({ filters, onClose }) {
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState(
    `<p>Bonjour {{name}},</p>\n<p>Merci pour votre inscription aux Fêtes de saint Léonard.</p>\n<p>…</p>\n<p>Bien à vous,<br>Les Amis du Prieuré saint Léonard</p>`
  );
  const [onlyPaid, setOnlyPaid] = useState(true);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const buildPayload = (dryRun) => ({
    subject, html,
    formula: filters.formula !== "all" ? filters.formula : undefined,
    day: filters.day !== "all" ? filters.day : undefined,
    only_paid: onlyPaid,
    dry_run: dryRun,
  });

  const doPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/presence/bulk-email", buildPayload(true));
      setPreview(data);
    } finally { setBusy(false); }
  };

  const doSend = async () => {
    if (!subject.trim()) { alert("Veuillez saisir un sujet"); return; }
    if (!window.confirm(`Envoyer à ${preview?.recipients_count ?? "?"} destinataires ?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/presence/bulk-email", buildPayload(false));
      setResult(data);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="presence-email-modal"
         className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(26,28,32,0.65)" }}>
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto"
           style={{ border: "1px solid var(--border)" }}>
        <div className="p-6 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="font-caps text-[0.7rem]" style={{ color: "var(--cinnabar)" }}>Envoi groupé</div>
            <h3 className="font-display text-2xl">E-mail aux inscrits</h3>
          </div>
          <button onClick={onClose} data-testid="email-modal-close" className="btn btn-ghost">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="font-body text-sm p-3" style={{ background: "var(--parchment-2)" }}>
            <strong>Filtre actif :</strong>{" "}
            {filters.formula !== "all" ? `formule=${filters.formula}` : "toutes formules"}
            {" · "}
            {filters.day !== "all" ? `jour=${filters.day}` : "tous jours"}
            {" · "}
            {onlyPaid ? "uniquement payés" : "tous statuts"}
          </div>

          <div>
            <label className="label-caps">Sujet</label>
            <input data-testid="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
                   className="input-classic" placeholder="Ex : Informations pratiques pour les Fêtes" />
          </div>

          <div>
            <label className="label-caps">Corps HTML (utilisez <code>{`{{name}}`}</code> pour personnaliser)</label>
            <textarea data-testid="email-body" value={html} onChange={(e) => setHtml(e.target.value)}
                      rows={9} className="input-classic font-mono text-sm" style={{ resize: "vertical" }} />
          </div>

          <label className="flex items-center gap-2 font-body text-sm">
            <input data-testid="email-only-paid" type="checkbox" checked={onlyPaid}
                   onChange={(e) => setOnlyPaid(e.target.checked)} />
            N'envoyer qu'aux paiements confirmés
          </label>

          {preview && (
            <div className="p-3" style={{ background: "var(--parchment-2)" }}>
              <div className="font-caps text-[0.7rem] mb-2" style={{ color: "var(--gold)" }}>Prévisualisation</div>
              <div className="font-display text-xl">{preview.recipients_count} destinataires</div>
              <ul className="mt-2 font-body text-xs">
                {preview.recipients_preview?.slice(0, 5).map(([em, nm]) => (
                  <li key={em} className="truncate">• {nm || "—"} &lt;{em}&gt;</li>
                ))}
                {preview.recipients_count > 5 && (
                  <li className="italic" style={{ color: "var(--muted)" }}>… et {preview.recipients_count - 5} de plus</li>
                )}
              </ul>
            </div>
          )}

          {result && (
            <div data-testid="email-result" className="p-3"
                 style={{ background: result.configured ? "var(--parchment-2)" : "#FFE4E4" }}>
              <div className="font-display text-lg">
                {result.configured
                  ? `✓ ${result.sent} e-mail(s) envoyé(s)`
                  : `⏳ ${result.queued} destinataires sauvegardés`}
              </div>
              <p className="font-body text-sm" style={{ color: "var(--ink-2)" }}>
                {result.message ||
                 (result.errors_count ? `${result.errors_count} erreur(s) — voir logs.` : "Tous les envois ont réussi.")}
              </p>
            </div>
          )}
        </div>
        <div className="p-6 border-t flex gap-3 justify-end" style={{ borderColor: "var(--border)" }}>
          <button onClick={onClose} className="btn btn-ghost">Fermer</button>
          <button data-testid="email-preview" onClick={doPreview} disabled={busy} className="btn btn-outline">
            Prévisualiser
          </button>
          <button data-testid="email-send" onClick={doSend} disabled={busy || !preview} className="btn btn-primary">
            Envoyer
          </button>
        </div>
      </div>
    </div>
  );
}
